"use client"

import { useState, useEffect, Suspense, useRef } from "react"
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate, useLocation } from "react-router-dom"
import "./App.css"
import "firebase/compat/auth"
import { auth, db, ref, onValue, get } from "./Firebase/firebase-config"
import { onAuthStateChanged } from "firebase/auth"
import ScrollToTop from "./ScrollToTop"
import Navbar from "./NavFooter/Navbar"
import Footer from "./NavFooter/Footer"
import "@fortawesome/fontawesome-free/css/all.min.css"
import "bootstrap/dist/css/bootstrap.min.css"
import "bootstrap/dist/js/bootstrap.bundle.min"
import SmoothScroll from "smooth-scroll"

// Landing Page Components
import { Navigation } from "./LandingPage/navigation"
import { Header } from "./LandingPage/header"
import { Features } from "./LandingPage/features"
import { About } from "./LandingPage/about"
import { Services } from "./LandingPage/services"
import { OurApp } from "./LandingPage/ourApp"
import { Partners } from "./LandingPage/Partners"
import { Contact } from "./LandingPage/contact"
import { LoanInfo } from "./LandingPage/LoanInfo"

import Dashboard from "./HomePage/Dashboard"
import NewQuery from "./Components/NewQuery"
import NeedHelp from "./Components/NeedHelp"
import TransportGoods from "./Components/TransportGoods"
import AltAlarm from "./Components/AltAlarm"
import Finance from "./Finance/Finance"
import LoanForm from "./Components/Finance"
import NewOffers from "./Components/NewOffers"
import PreviousOrders from "./Components/PreviousOrders"
import RateHistory from "./Components/RateHistory"
import RaiseIssuePage from "./Components/RaiseIssuePage"
import UserProfile from "./Firebase/UserProfile"
import UserNotifications from "./Notification/UserNotifications"
import NewProducts from "./Components/NewProducts"
import TermAndCondition from "./TermAndCondition/TermAndCondition"
import NewOrder from "./ProductOrder/NewOrder"
import ProductOrderForm from "./ProductOrder/RefactoredProductOrderForm"
import SteelOrderForm from "./ProductOrder/SteelOrderForm"
import MachineryOrderForm from "./ProductOrder/MachineryOrderForm"
import ContactUs from "./Support/ContactUs"
import PrivacyPolicy from "./TermAndCondition/PrivacyPolicy"
import OrderStatus from "./Components/OrderStatus"
import DeleteAccount from "./Firebase/DeleteAccount"
import Login from "./Auth/Login"
import ProfileCompletionScreen from "./Auth/ProfileCompletionScreen"
import SignupScreen from "./Auth/Signup"
import AuthService from "./Auth/AuthService"
import QuotationManagement from "./Components/QuotationManagement"
import LoanHistory from "./Finance/LoanHistory"
import DocumentsPage from "./HomePage/DocumentsPage"
import Chatbot from "./ChatBot/Chatbot"
import { FaComments } from "react-icons/fa"
import PartnerManagement from "./PartnerSetup/PartnerManagement"
import PartnerDashboard from "./PartnerSetup/PartnerDashboard"
import PartnerLogin from "./PartnerSetup/PartnerLogin"
import UserStockStatus from "./StockStatus/UserStockStatus"
import PartnerProtectedRoute from "./PartnerSetup/PartnerProtectedRoute"
import AddFirm from "./Firms/AddFirms"
import { AccountProvider } from "./context/AccountContext"
import { MainOnlyRoute } from "./ProtectedRoute"
import { useCurrentUid } from "./hooks/useCurrentUid"
import Downloads from "./HomePage/Downloads"
import UserPurchaseOrders from "./ActivePO/UserPurchaseOrders"
import { IoCloseOutline } from "react-icons/io5"


function App() {
  const currentUserId = useCurrentUid()
  const [isAuthenticated, setIsAuthenticated] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const navigate = useNavigate()
  const [newOfferDetected, setNewOfferDetected] = useState(false)
  const location = useLocation()
  const locationRef = useRef(location)
  const [totalUnread, setTotalUnread] = useState(0)
  const [userId, setUserId] = useState(null)
  const [isPartner, setIsPartner] = useState(false)



  const authFreeRoutes = [
    "/signup",
    "/login",
    "/profile-complete",
    "/term&condition",
    "/privacy-policy",
    "/partner-login",
  ]


  const isPartnerUser = async (uid) => {
    const snapshot = await get(ref(db, "profileDashboard"))
    if (!snapshot.exists()) return false

    let found = false
    snapshot.forEach((mainUserSnap) => {
      const partners = mainUserSnap.child("partners").val()
      if (partners && partners[uid]) {
        found = true
      }
    })
    return found
  }

  useEffect(() => {
    locationRef.current = location
  }, [location])

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setIsLoading(true)
      setIsAuthenticated(false)
      setIsPartner(false)

      if (!user) {
        setIsAuthenticated(false)
        if (!authFreeRoutes.includes(locationRef.current.pathname) && locationRef.current.pathname !== "/") {
          navigate("/", { replace: true })
        }
        setIsLoading(false)
        return
      }

      try {
        const uid = user.uid
        setUserId(uid)

        // STEP 1: Check if this UID is a PARTNER (stored inside any main user's partners node)
        const isPartnerCheck = await isPartnerUser(uid)

        if (isPartnerCheck) {
          // This is a valid PARTNER
          setIsPartner(true)
          setIsAuthenticated(true)
          navigate("/partner-dashboard", { replace: true })
          setIsLoading(false)
          return
        }
        if (locationRef.current.pathname === "/partner-login") {
          // setIsAuthenticated(true)
          setIsLoading(false)
          return  // Let PartnerLogin page handle everything
        }


        // STEP 2: If NOT a partner → check if they are a MAIN USER (has own profileDashboard node)
        const hasOwnProfile = await AuthService.checkUserExists(uid)

        if (hasOwnProfile) {
          // This is a MAIN USER with complete profile
          setIsPartner(false)
          setIsAuthenticated(true)


          // Else stay on current private page
        } else {
          // This is a MAIN USER but profile not complete → force profile completion
          setIsPartner(false)
          setIsAuthenticated(true)

          if (locationRef.current.pathname !== "/profile-complete") {
            navigate("/profile-complete", { replace: true })
          }
        }

      } catch (error) {
        console.error("Auth state error:", error)
        await auth.signOut()
        navigate("/", { replace: true })
      } finally {
        setIsLoading(false)
        setIsTransitioning(false)
      }
    })

    return unsubscribe
  }, [navigate])
  useEffect(() => {
    if (!currentUserId) return

    const userChatsRef = ref(db, `userchatbox/${currentUserId}`)
    const groupChatsRef = ref(db, `groupChats`)

    const getUnreadCount = (data, isGroup = false, currentUserId) => {
      let unread = 0
      if (!data) return 0

      Object.entries(data).forEach(([chatId, chat]) => {
        // For group chats, check if user is a member
        if (isGroup && chat.members && !chat.members.includes(currentUserId)) {
          return // Skip groups where user is not a member
        }

        const messages = isGroup ? chat.messages : chat
        if (!messages) return

        Object.entries(messages).forEach(([msgId, msg]) => {
          if (msg && msg.sender && msg.sender !== currentUserId) {
            // Check if current user has read this message
            const readBy = msg.readBy || []
            if (!readBy.includes(currentUserId)) {
              unread++
            }
          }
        })
      })

      return unread
    }

    const updateUnread = async () => {
      try {
        const userSnap = await get(userChatsRef)
        const userData = userSnap.val()

        const userUnread = getUnreadCount(userData, false, currentUserId)

        const groupSnap = await get(groupChatsRef)
        const groupData = groupSnap.val()

        const groupUnread = getUnreadCount(groupData, true, currentUserId)

        setTotalUnread(userUnread + groupUnread)
      } catch (e) {
        console.error("Unread count error:", e)
      }
    }

    updateUnread()

    const unsubUser = onValue(userChatsRef, () => updateUnread())
    const unsubGroup = onValue(groupChatsRef, () => updateUnread())

    return () => {
      unsubUser()
      unsubGroup()
    }
  }, [currentUserId])

  useEffect(() => {
    const smoothScrollInit = setTimeout(() => {
      new SmoothScroll('a[href*="#"]', {
        speed: 800,
        speedAsDuration: true,
        updateURL: false,
        easing: "easeInOutCubic",
        offset: 70,
      })
    }, 500)

    return () => clearTimeout(smoothScrollInit)
  }, [])

  const logout = async () => {
    try {
      setIsLoading(true)
      await auth.signOut()
      navigate("/")
    } catch (error) {
      console.error("Error during logout:", error)
    } finally {
      setIsLoading(false)
    }
  }
  const CheckProfileAndRender = ({ component, fallback }) => {
    const [allowed, setAllowed] = useState(null)

    useEffect(() => {
      const check = async () => {
        const user = auth.currentUser
        if (user) {
          const exists = await AuthService.checkUserExists(user.uid)
          setAllowed(exists)
        } else {
          setAllowed(false)
        }
      }
      check()
    }, [])

    if (allowed === null) return <div></div>
    return allowed ? component : fallback
  }

  const LoadingScreen = () => (
    <div className="app-loading-transition">
      <div className="app-loading-spinner">
        <div className="app-spinner"></div>
      </div>
    </div>
  )

  if (isLoading || isTransitioning || isAuthenticated === null) {
    return <LoadingScreen />
  }
  const handleChatClick = () => {
    if (location.pathname === "/dashboard/Chatbot") {
      navigate(-1)
    } else {
      navigate("/dashboard/Chatbot")
    }
  }

  const isAuthFreeRoute = authFreeRoutes.includes(location.pathname)
  return (
    <div className="app-container">
      {isAuthenticated && !isAuthFreeRoute && !isPartner && (
        <Navbar isAuthenticated={isAuthenticated} logout={logout} />
      )}

      <div className="main-content">
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            <Route path="/signup" element={<SignupScreen onSignupSuccess={() => setIsTransitioning(true)} />} />
            <Route path="/login" element={<Login onSignupSuccess={() => setIsTransitioning(true)} />} />
            <Route
              path="/profile-complete"
              element={<ProfileCompletionScreen onSignupSuccess={() => setIsTransitioning(true)} />}
            />
            <Route path="/term&condition" element={<TermAndCondition />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/partner-login" element={<PartnerLogin />} />
            <Route
              path="/add-firms"
              element={
                <MainOnlyRoute>
                  <AddFirm />
                </MainOnlyRoute>
              }
            />

            <Route path="/profile" element={isAuthenticated ? <UserProfile /> : <Navigate to="/" />} />
            <Route path="/delete-account" element={isAuthenticated ? <DeleteAccount /> : <Navigate to="/" />} />
            <Route
              path="/partner-dashboard"
              element={isAuthenticated && isPartner ? <PartnerProtectedRoute>
                <PartnerDashboard />
              </PartnerProtectedRoute> : <Navigate to="/" />}
            />
            <Route path="/dashboard" element={isAuthenticated && !isPartner ? <Dashboard /> : <Navigate to="/" />}>
              <Route path="product-order" element={<NewOrder />} />
              <Route path="product-order-form" element={<ProductOrderForm />} />
              <Route path="PO" element={<UserPurchaseOrders />} />
              <Route path="downloads" element={<Downloads />} />
              <Route path="steel-order-form" element={<SteelOrderForm />} />
              <Route path="machinery-order-form" element={<MachineryOrderForm />} />
              <Route path="new-query" element={<NewQuery />} />
              <Route path="need-help" element={<NeedHelp />} />
              <Route path="transport-goods" element={<TransportGoods />} />
              <Route path="alt-alarm" element={<AltAlarm />} />
              <Route path="finance" element={<Finance />} />
              <Route path="loan-history" element={<LoanHistory />} />
              <Route path="order-status" element={<OrderStatus />} />
              <Route path="stock-status" element={<UserStockStatus />} />
              <Route path="order-status/:partnerUid" element={<OrderStatus />} />
              <Route path="partner-management" element={<PartnerManagement />} />
              <Route path="quote-request" element={<QuotationManagement />} />
              <Route path="newoffers" element={<NewOffers onViewOffers={() => setNewOfferDetected(false)} />} />
              <Route path="previous-orders" element={<PreviousOrders />} />
              <Route path="rate-history" element={<RateHistory />} />
              <Route path="raise-issue" element={<RaiseIssuePage />} />
              <Route path="newSupplier-products" element={<NewProducts />} />
              <Route path="notifications" element={<UserNotifications />} />
              <Route path="contact-us" element={<ContactUs />} />
              <Route path="documents" element={<DocumentsPage />} />
              <Route path="apply-loan" element={<LoanForm />} />
              {/* <Route path="add-firms" element={<AddFirm />} /> */}
              <Route path="Chatbot" element={<Chatbot />} />
            </Route>

            <Route
              path="/"
              element={
                isAuthenticated ? (
                  <Navigate to={isPartner ? "/partner-dashboard" : "/dashboard"} replace />
                ) : (
                  <LandingPage />
                )
              }
            />

            <Route
              path="*"
              element={
                <Navigate to={isAuthenticated ? (isPartner ? "/partner-dashboard" : "/dashboard") : "/"} replace />
              }
            />
          </Routes>
        </Suspense>
        {/* Floating Chat Button - Smart Icon based on Chatbot state */}
{isAuthenticated && !isPartner && !isAuthFreeRoute && (
  <button 
    className={`chatbot-button ${totalUnread > 0 ? "chatbot-has-unread" : ""} ${location.pathname === "/dashboard/Chatbot" ? "chatbot-open" : ""}`}
    onClick={handleChatClick}
  >
    {location.pathname === "/dashboard/Chatbot" ? (
      // When Chatbot is OPEN → Show Close Icon
      <IoCloseOutline size={24} />
    ) : (
      // When Chatbot is CLOSED → Show Normal Chat Icon
      <FaComments size={22} />
    )}
    
    {/* Show unread badge only when chatbot is NOT open */}
    {totalUnread > 0 && location.pathname !== "/dashboard/Chatbot" && (
      <span className="chatbot-badge">{totalUnread > 99 ? "99+" : totalUnread}</span>
    )}
  </button>
)}
      </div>
      {isAuthenticated && !isAuthFreeRoute && !isPartner && (
        <Footer isAuthenticated={isAuthenticated} logout={logout} />
      )}
    </div>
  )
}

function LandingPage() {
  return (
    <div className="landingpage">
      <Navigation />
      <Header />
      <Features />
      <About />
      <Services />
      <Partners />
      <LoanInfo />
      <OurApp />
      <Contact />
    </div>
  )
}

function AppWrapper() {
  return (
    <Router>
      <ScrollToTop />
      <AccountProvider>
        <App />
      </AccountProvider>
    </Router>
  )
}

export default AppWrapper
